import * as React from 'react';
import { useLocation } from 'react-router-dom';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-charts/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-tree-view/themeAugmentation';
import { alpha } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Header from './components/Header';
import EnergyInsights from './components/EnergyInsights';
import ElectricityDashboard from './components/ElectricityDashboard';
import GasDashboard from './components/GasDashboard';
import Emissions from './components/Emissions';
import AccountDashboard from './components/AccountDashboard';
import AppTheme from '../shared-theme/AppTheme';
import Copyright from '../dashboard/internals/components/Copyright';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  surfacesCustomizations,
  treeViewCustomizations,
} from './theme/customizations';
import withAuth from '../withAuth'
import LoadingProgress from '../register/LoadingProgress';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...surfacesCustomizations,
  ...treeViewCustomizations,
};

const Dashboard = (props: { disableCustomTheme?: boolean }) => {
  const location = useLocation();
  const [showProgress] = React.useState(false);
  const [loadedToDate] = React.useState('');
  const [loadPercentComplete] = React.useState(0);
  const [selectedMenuItem, setSelectedMenuItem] = React.useState('tariffs');

  // Handle navigation state from other pages
  React.useEffect(() => {
    if (location.state?.selectedMenuItem) {
      setSelectedMenuItem(location.state.selectedMenuItem);
    }
  }, [location.state]);

  const handleMenuSelect = (menuItem: string) => {
    setSelectedMenuItem(menuItem);
  };

  const renderDashboardContent = () => {
    switch (selectedMenuItem) {
      case 'tariffs':
        return <EnergyInsights />;
      case 'electricity':
        return <ElectricityDashboard />;
      case 'gas':
        return <GasDashboard />;
      case 'emissions':
        return <Emissions />;
      case 'account':
        return <AccountDashboard onNavigate={handleMenuSelect} />;
      default:
        return <EnergyInsights />;
    }
  };


  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <LoadingProgress open={showProgress} loadedTo={loadedToDate} progress={loadPercentComplete} />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header onMenuSelect={handleMenuSelect} selectedMenuItem={selectedMenuItem} />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            paddingTop: { xs: 0, md: '64px' }, // Add padding to account for fixed header
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            backgroundImage: theme.palette.mode === 'light' 
              ? 'url(/light-bg.jpg)' 
              : 'url(/dark-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box sx={(theme) => ({ 
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'transparent'
            })}>
              <Box sx={(theme) => ({ 
                width: '100%', 
                maxWidth: '1200px',
                backgroundColor: theme.palette.surface,
                minHeight: 'calc(100vh - 64px)',
                px: { xs: 0, md: 3 },
                pb: 1
              })}>
                  {renderDashboardContent()}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>
      <Box
          component="footer"
          sx={{
            py: 1.5,
            px: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Copyright sx={{ mt: 0, mb: 0 }} />
        </Box>
    </AppTheme>
  );
}

export default withAuth(Dashboard);
